import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BonusCard } from './components/BonusCard';
import { Footer } from './components/Footer';
import { CheckIcon, Heart, Star, ShieldCheck, Sparkles, BookOpen } from 'lucide-react';
// Simplified animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function App() {
  return <div className="min-h-screen bg-[#F7F3EF] text-[#3A3A3A] font-sans selection:bg-[#E8CFCF] selection:text-[#3A3A3A]" data-id="element-0">
      {/* HERO SECTION */}
      <header className="pt-20 pb-16 px-4 md:pt-32 md:pb-24 bg-white border-b border-[#E8CFCF]/40" data-id="element-1">
        <div className="max-w-5xl mx-auto text-center" data-id="element-2">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-3">
            <motion.div variants={fadeInUp} className="flex justify-center" data-id="element-4">
              <span className="inline-flex items-center gap-2 py-1 px-4 rounded-full bg-[#F5EAEA] text-[#D88C7A] text-sm font-medium border border-[#E8CFCF]" data-id="element-5">
                <Sparkles className="w-4 h-4" data-id="element-6" />
                Crianza Respetuosa y Consciente
              </span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl font-bold text-[#3A3A3A] leading-[1.1] tracking-tight max-w-4xl mx-auto" data-id="element-7">
              ¿Cansada de gritar y luego sentir culpa?{' '}
              <span className="text-[#D88C7A] relative inline-block" data-id="element-8">
                Aprende a criar hijos emocionalmente fuertes sin perder la calma
                💛
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-[#E8CFCF] -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-9">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" data-id="element-10" />
                </svg>
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#3A3A3A]/70 max-w-2xl mx-auto leading-relaxed" data-id="element-11">
              Descubre estrategias prácticas y amorosas para manejar berrinches
              y emociones intensas sin castigos ni frustración.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4" data-id="element-12">
              <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-lg py-4 px-8 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 text-center" data-id="element-13">
                QUIERO LA GUÍA AHORA
              </a>
              <p className="text-sm text-[#3A3A3A]/50 font-medium" data-id="element-14">
                ⚡️ Descarga inmediata + 3 bonos gratis
              </p>
            </motion.div>
          </motion.div>
        </div>
      </header>

      {/* SOCIAL PROOF / PROMISE */}
      <section className="py-12 px-4 bg-[#EDF1EA] border-b border-[#A8B5A2]/20" data-id="element-15">
        <div className="max-w-4xl mx-auto text-center" data-id="element-16">
          <p className="text-[#3A3A3A]/70 font-medium mb-8" data-id="element-17">
            Lo que lograrás con este método:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-18">
            {[{
            icon: '😌',
            text: 'Más paciencia'
          }, {
            icon: '🧠',
            text: 'Inteligencia emocional'
          }, {
            icon: '🤝',
            text: 'Conexión profunda'
          }, {
            icon: '🏠',
            text: 'Hogar en calma'
          }].map((item, i) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-19">
                <span className="text-2xl" data-id="element-20">{item.icon}</span>
                <span className="text-[#3A3A3A] font-semibold" data-id="element-21">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* PROBLEM AWARENESS */}
      <section className="py-20 px-4 bg-white" data-id="element-22">
        <div className="max-w-5xl mx-auto" data-id="element-23">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-24">
            <div className="order-2 md:order-1" data-id="element-25">
              <h2 className="text-3xl font-bold text-[#3A3A3A] mb-6" data-id="element-26">
                ¿Te sientes identificada?
              </h2>
              <div className="space-y-4" data-id="element-27">
                {['Terminas el día agotada y con culpa por haber gritado.', 'Tus hijos tienen berrinches y no sabes cómo calmarlos.', 'Quieres educar diferente a tus padres pero te faltan herramientas.', 'Sientes que pierdes el control cuando tus hijos no obedecen.'].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-[#F5EAEA] border border-[#E8CFCF]" data-id="element-28">
                    <div className="bg-[#E8CFCF] text-[#D88C7A] p-1 rounded-full mt-0.5" data-id="element-29">
                      <CheckIcon className="w-4 h-4" data-id="element-30" />
                    </div>
                    <p className="text-[#3A3A3A]/80" data-id="element-31">{item}</p>
                  </div>)}
              </div>
              <div className="mt-8 p-6 bg-[#EDF1EA] rounded-xl border border-[#A8B5A2]/30" data-id="element-32">
                <p className="text-[#3A3A3A] font-medium" data-id="element-33">
                  💛 No es tu culpa. Nadie nos enseñó a gestionar las emociones
                  de nuestros hijos (ni las nuestras). Pero hoy puedes cambiar
                  eso.
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2 flex justify-center" data-id="element-34">
              <div className="relative w-full max-w-md aspect-[3/4] bg-[#F5EAEA] rounded-2xl overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-35">
                <img src="https://cdn.magicpatterns.com/uploads/rit46wTNsnyXQQHNnMTmkB/IMG_1743.png" alt="Guía de Crianza con Inteligencia Emocional" className="w-full h-full object-cover" data-id="element-36" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN PRODUCT SHOWCASE */}
      <section className="py-20 px-4 bg-[#EDF1EA]" data-id="element-37">
        <div className="max-w-6xl mx-auto" data-id="element-38">
          <div className="text-center mb-16" data-id="element-39">
            <span className="text-[#A8B5A2] font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-40">
              La Solución Completa
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-4" data-id="element-41">
              Guía de Crianza con Inteligencia Emocional
            </h2>
            <p className="text-[#3A3A3A]/70 max-w-2xl mx-auto text-lg" data-id="element-42">
              Un manual práctico, directo y lleno de ejemplos reales para
              transformar la relación con tus hijos.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-[#A8B5A2]/20 flex flex-col md:flex-row gap-12 items-center" data-id="element-43">
            <div className="w-full md:w-1/2" data-id="element-44">
              <img src="https://cdn.magicpatterns.com/uploads/962ghSQKF3z4ygCbm9XJm1/IMG_1743.png" alt="Portada de la Guía de Crianza" className="w-full rounded-xl shadow-lg transform md:-rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-45" />
            </div>
            <div className="w-full md:w-1/2 space-y-6" data-id="element-46">
              <h3 className="text-2xl font-bold text-[#3A3A3A]" data-id="element-47">
                ¿Qué aprenderás?
              </h3>
              <ul className="space-y-4" data-id="element-48">
                {['Cómo validar emociones sin ser permisiva.', 'Herramientas para gestionar berrinches en público y en casa.', 'Guiones exactos de qué decir en momentos de crisis.', 'Técnicas para mantener tu propia calma cuando todo explota.', 'Cómo establecer límites firmes pero amorosos.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-49">
                    <div className="bg-[#EDF1EA] text-[#A8B5A2] p-1 rounded-full mt-1" data-id="element-50">
                      <CheckIcon className="w-3 h-3" data-id="element-51" />
                    </div>
                    <span className="text-[#3A3A3A]/80 text-lg" data-id="element-52">{item}</span>
                  </li>)}
              </ul>
              <div className="pt-6" data-id="element-53">
                <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all hover:scale-105 text-center" data-id="element-54">
                  QUIERO EMPEZAR A LEER
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-20 px-4 bg-[#F7F3EF] border-y border-[#E8CFCF]/30" data-id="element-55">
        <div className="max-w-6xl mx-auto" data-id="element-56">
          <div className="text-center mb-16" data-id="element-57">
            <span className="text-[#D88C7A] font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-58">
              Historias Reales
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-4" data-id="element-59">
              Mamás que ya transformaron su crianza
            </h2>
            <div className="flex justify-center gap-1 text-amber-400 mb-2" data-id="element-60">
              <Star className="w-6 h-6 fill-current" data-id="element-61" />
              <Star className="w-6 h-6 fill-current" data-id="element-62" />
              <Star className="w-6 h-6 fill-current" data-id="element-63" />
              <Star className="w-6 h-6 fill-current" data-id="element-64" />
              <Star className="w-6 h-6 fill-current" data-id="element-65" />
            </div>
            <p className="text-[#3A3A3A]/60" data-id="element-66">Más de 500 mamás felices</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-67">
            {/* Testimonio 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-68">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-69">
                <span className="text-2xl" data-id="element-70">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-71">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-72">
                  <Star className="w-4 h-4 fill-current" data-id="element-73" />
                  <Star className="w-4 h-4 fill-current" data-id="element-74" />
                  <Star className="w-4 h-4 fill-current" data-id="element-75" />
                  <Star className="w-4 h-4 fill-current" data-id="element-76" />
                  <Star className="w-4 h-4 fill-current" data-id="element-77" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-78">
                  Transformación emocional
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-79">
                "Esta guía llegó justo cuando sentía que estaba fallando como
                mamá. Aprendí a escuchar a mis hijos sin gritar y a manejar mis
                propias emociones. Hoy mi casa se siente más tranquila y
                conectada. No es solo una guía, es un abrazo para el alma."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-80">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-81">
                  MG
                </div>
                <div data-id="element-82">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-83">Mariana G.</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-84">Mamá de 2 niños</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-85">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-86">
                <span className="text-2xl" data-id="element-87">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-88">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-89">
                  <Star className="w-4 h-4 fill-current" data-id="element-90" />
                  <Star className="w-4 h-4 fill-current" data-id="element-91" />
                  <Star className="w-4 h-4 fill-current" data-id="element-92" />
                  <Star className="w-4 h-4 fill-current" data-id="element-93" />
                  <Star className="w-4 h-4 fill-current" data-id="element-94" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-95">
                  Resultados prácticos
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-96">
                "Lo que más me gustó es que no es teoría complicada. Son
                palabras exactas para usar en momentos difíciles. Las tarjetas
                emocionales cambiaron por completo la forma en que mis hijos
                expresan lo que sienten."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-97">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-98">
                  LP
                </div>
                <div data-id="element-99">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-100">Laura P.</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-101">Mamá y educadora</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-102">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-103">
                <span className="text-2xl" data-id="element-104">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-105">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-106">
                  <Star className="w-4 h-4 fill-current" data-id="element-107" />
                  <Star className="w-4 h-4 fill-current" data-id="element-108" />
                  <Star className="w-4 h-4 fill-current" data-id="element-109" />
                  <Star className="w-4 h-4 fill-current" data-id="element-110" />
                  <Star className="w-4 h-4 fill-current" data-id="element-111" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-112">
                  Alivio para la mamá
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-113">
                "Por primera vez sentí que alguien entendía lo agotador que es
                criar con amor. La guía de relajación para mamás fue mi
                salvación. Ahora reacciono desde la calma y no desde el
                cansancio."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-114">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-115">
                  AR
                </div>
                <div data-id="element-116">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-117">Andrea R.</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-118">Mamá primeriza</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonio 4 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-[#E8CFCF]/40 relative" data-id="element-119">
              <div className="absolute -top-4 left-8 bg-[#F5EAEA] text-[#D88C7A] p-2 rounded-lg" data-id="element-120">
                <span className="text-2xl" data-id="element-121">❝</span>
              </div>
              <div className="mb-4 pt-4" data-id="element-122">
                <div className="flex gap-1 text-amber-400 mb-2" data-id="element-123">
                  <Star className="w-4 h-4 fill-current" data-id="element-124" />
                  <Star className="w-4 h-4 fill-current" data-id="element-125" />
                  <Star className="w-4 h-4 fill-current" data-id="element-126" />
                  <Star className="w-4 h-4 fill-current" data-id="element-127" />
                  <Star className="w-4 h-4 fill-current" data-id="element-128" />
                </div>
                <h3 className="font-bold text-[#3A3A3A] text-lg" data-id="element-129">
                  Confianza y conexión
                </h3>
              </div>
              <p className="text-[#3A3A3A]/70 mb-6 italic leading-relaxed" data-id="element-130">
                "Después de aplicar esta guía, mi hijo empezó a hablar de sus
                emociones sin miedo. Yo también aprendí a perdonarme y a confiar
                en mi proceso como mamá. La recomiendo a todas."
              </p>
              <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-4" data-id="element-131">
                <div className="w-10 h-10 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-132">
                  CM
                </div>
                <div data-id="element-133">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-134">
                    Carolina M.
                  </p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-135">
                    Mamá de un niño sensible
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-20 px-4 bg-white" data-id="element-136">
        <div className="max-w-6xl mx-auto" data-id="element-137">
          <div className="text-center mb-12" data-id="element-138">
            <span className="bg-[#F5EAEA] text-[#D88C7A] px-4 py-1 rounded-full text-sm font-bold mb-4 inline-block" data-id="element-139">
              🎁 REGALOS EXCLUSIVOS
            </span>
            <h2 className="text-3xl font-bold text-[#3A3A3A]" data-id="element-140">
              Llévate estos 3 bonos GRATIS hoy
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-141">
            <BonusCard title="Tarjetas Emocionales Ilustradas" value="$35" icon="🎨" description="💡 Perfectas para el hogar, rutinas y momentos difíciles." imageUrl="https://cdn.magicpatterns.com/uploads/urr9uCPGXFn9ek73skaMbG/IMG_0180.jpg" benefits={['Identificar lo que sienten', 'Expresar emociones sin berrinches', 'Desarrollar inteligencia emocional desde pequeños']} delay={0.1} data-id="element-142" />
            <BonusCard title="Cómo Crear Tus Propias Tarjetas Emocionales en Casa" value="$47" icon="✂️" description="✨ Una habilidad que podrás usar para siempre." imageUrl="https://cdn.magicpatterns.com/uploads/tDs3nGtTJ1vuHJUsdaKjBf/IMG_0181.jpg" benefits={['Diseñar tarjetas personalizadas según la edad de tus hijos', 'Adaptarlas a situaciones reales del día a día', 'Convertirlas en una herramienta de conexión familiar']} delay={0.2} data-id="element-143" />
            <BonusCard title="Relajación Emocional para Mamás" value="$55" icon="🧘‍♀️" description="🌿 Diseñado para mamás reales, con poco tiempo." imageUrl="https://cdn.magicpatterns.com/uploads/kkUVLS6SFZZtWuAbAjNMKE/IMG_0182.jpg" benefits={['Ejercicios de descarga emocional', 'Técnicas de calma después de días difíciles', 'Prácticas cortas para recuperar energía mental']} delay={0.3} data-id="element-144" />
          </div>
        </div>
      </section>

      {/* PRICING / CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#F7F3EF] via-[#F5EAEA] to-[#EDF1EA] text-[#3A3A3A] relative overflow-hidden" data-id="element-145">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#D88C7A_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-146"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-147">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#3A3A3A]" data-id="element-148">
            Transforma tu maternidad hoy mismo
          </h2>
          <p className="text-[#3A3A3A]/70 text-xl mb-12 max-w-2xl mx-auto" data-id="element-149">
            Deja atrás la culpa y los gritos. Empieza a construir la relación
            que siempre soñaste tener con tus hijos.
          </p>

          <div className="bg-white text-[#3A3A3A] rounded-3xl p-8 md:p-12 max-w-lg mx-auto shadow-2xl border border-[#E8CFCF]/40" data-id="element-150">
            <div className="flex justify-center items-baseline gap-2 mb-2" data-id="element-151">
              <span className="text-5xl font-bold text-[#D88C7A]" data-id="element-152">$12.75</span>
              <span className="text-[#3A3A3A]/40 line-through text-xl" data-id="element-153">
                $137
              </span>
            </div>
            <p className="text-[#3A3A3A]/50 mb-8 font-medium" data-id="element-154">
              Oferta por tiempo limitado
            </p>

            <ul className="text-left space-y-3 mb-8 max-w-xs mx-auto" data-id="element-155">
              <li className="flex items-center gap-3" data-id="element-156">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-157" />
                <span data-id="element-158">Guía Digital Completa (PDF)</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-159">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-160" />
                <span data-id="element-161">Bono 1-Tarjetas Emocionales Ilustradas</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-162">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-163" />
                <span data-id="element-164">
                  Bono 2-Cómo Crear Tus Propias Tarjetas Emocionales en Casa
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-165">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-166" />
                <span data-id="element-167">Bono 3-Relajación Emocional para Mamás</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-168">
                <CheckIcon className="w-5 h-5 text-[#A8B5A2]" data-id="element-169" />
                <span data-id="element-170">Acceso de por vida</span>
              </li>
            </ul>

            <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-8 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-171">
              DESCARGAR TODO AHORA
            </a>
            <p className="mt-4 text-xs text-[#3A3A3A]/40" data-id="element-172">
              🔒 Pago 100% seguro • Acceso de por vida • Guía Digital en formato
              PDF 💛
            </p>
          </div>
        </div>
      </section>

      {/* PREMIUM UPGRADE SECTION */}
      <section className="py-20 px-4 bg-white border-t border-[#E8CFCF]/30" data-id="element-173">
        <div className="max-w-4xl mx-auto" data-id="element-174">
          <div className="text-center mb-12" data-id="element-175">
            <span className="bg-gradient-to-r from-[#D88C7A] to-[#C57665] text-white px-6 py-2.5 rounded-full text-sm font-bold mb-4 inline-flex items-center gap-2 shadow-lg shadow-[#D88C7A]/30 uppercase tracking-widest animate-pulse" data-id="element-176">
              💎 VERSIÓN PREMIUM — OFERTA EXCLUSIVA
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3A3A3A] mb-6" data-id="element-177">
              Convierte los momentos más difíciles con tu hijo en oportunidades
              de conexión, calma y aprendizaje
            </h2>
            <p className="text-[#3A3A3A]/70 text-xl max-w-3xl mx-auto" data-id="element-178">
              Lleva tu experiencia al siguiente nivel con herramientas prácticas
              que te guían exactamente qué hacer en tiempo real, cuando más lo
              necesitas.
            </p>
            <div className="mt-10 flex justify-center" data-id="element-179">
              <img src="https://cdn.magicpatterns.com/uploads/7apuq5UiPfzj49buN4xzSF/IMG_1737.jpg" alt="Técnicas para Regular las Molestias de tu Hijo - Versión Premium" className="w-full max-w-md rounded-2xl shadow-xl" data-id="element-180" />
            </div>
          </div>

          {/* QUÉ INCLUYE */}
          <div className="bg-[#EDF1EA] rounded-3xl p-8 mb-12 border border-[#A8B5A2]/30" data-id="element-181">
            <h3 className="text-xl font-bold text-[#3A3A3A] mb-6 text-center" data-id="element-182">
              Al acceder a la versión premium obtienes:
            </h3>
            <div className="grid md:grid-cols-2 gap-4" data-id="element-183">
              {['Ebook completo de Crianza con Inteligencia Emocional', 'Todos los bonos descargables incluidos', 'Herramientas prácticas para aplicar en casa', 'Acceso inmediato y de por vida'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-184">
                  <CheckIcon className="w-5 h-5 text-[#A8B5A2] flex-shrink-0" data-id="element-185" />
                  <span className="text-[#3A3A3A]/80 font-medium" data-id="element-186">{item}</span>
                </div>)}
            </div>
          </div>

          {/* BLOQUE DESTACADO - BONO PREMIUM */}
          <div className="bg-[#F5EAEA] rounded-3xl p-8 md:p-10 mb-12 border-2 border-[#E8CFCF] relative overflow-hidden shadow-lg" data-id="element-187">
            <div className="absolute top-0 right-0 bg-[#D88C7A] text-white px-6 py-2 rounded-bl-2xl font-bold text-sm" data-id="element-188">
              NUEVO BONO PREMIUM EXCLUSIVO
            </div>
            <div className="mt-6 md:mt-2 flex flex-col md:flex-row gap-8 items-center" data-id="element-189">
              <div className="w-full md:w-2/5 flex-shrink-0" data-id="element-190">
                <img src="https://cdn.magicpatterns.com/uploads/nM4jdonaD8gYfAZbFuhLAh/IMG_1737.jpg" alt="Técnicas para Regular las Molestias de tu Hijo" className="w-full rounded-2xl shadow-lg" data-id="element-191" />
              </div>
              <div className="w-full md:w-3/5" data-id="element-192">
                <h3 className="text-2xl md:text-3xl font-bold text-[#3A3A3A] mb-4" data-id="element-193">
                  Técnicas para Regular las Molestias de tu Hijo
                </h3>
                <p className="text-[#3A3A3A]/80 text-lg mb-8" data-id="element-194">
                  Un sistema práctico de tarjetas imprimibles diseñado para
                  ayudarte a actuar con seguridad en momentos de rabietas,
                  frustración o llanto.
                </p>

                <div className="space-y-4 mb-8" data-id="element-195">
                  {['Frases exactas que puedes decir en momentos difíciles', 'Técnicas efectivas para calmar sin gritar ni castigar', 'Estrategias para acompañar emociones intensas', 'Herramientas visuales fáciles de usar con tu hijo'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-196">
                      <div className="bg-[#E8CFCF] text-[#D88C7A] p-1 rounded-full mt-0.5 flex-shrink-0" data-id="element-197">
                        <CheckIcon className="w-4 h-4" data-id="element-198" />
                      </div>
                      <span className="text-[#3A3A3A]/80" data-id="element-199">{item}</span>
                    </div>)}
                </div>

                <div className="bg-white/60 rounded-xl p-6 border border-[#E8CFCF]/50" data-id="element-200">
                  <p className="text-[#D88C7A] font-bold text-lg mb-2" data-id="element-201">
                    👉 Ya no tendrás que improvisar
                  </p>
                  <p className="text-[#D88C7A] font-bold text-lg" data-id="element-202">
                    👉 Tendrás claridad en cada situación
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* BLOQUE EMOCIONAL */}
          <div className="text-center mb-12 max-w-2xl mx-auto" data-id="element-203">
            <p className="text-xl text-[#3A3A3A] font-medium mb-6" data-id="element-204">
              Sabemos lo difícil que puede ser cuando tu hijo:
            </p>
            <ul className="text-left space-y-3 mb-8 mx-auto inline-block" data-id="element-205">
              <li className="text-[#3A3A3A]/80 text-lg" data-id="element-206">
                • tiene una rabieta inesperada
              </li>
              <li className="text-[#3A3A3A]/80 text-lg" data-id="element-207">• llora sin parar</li>
              <li className="text-[#3A3A3A]/80 text-lg" data-id="element-208">
                • se frustra y no sabe cómo expresarse
              </li>
            </ul>
            <p className="text-[#3A3A3A]/80 text-lg mb-6 italic" data-id="element-209">
              Y tú solo quieres ayudar… pero no sabes exactamente cómo hacerlo.
            </p>
            <p className="text-xl font-bold text-[#D88C7A] mb-8" data-id="element-210">
              💛 Este bono fue creado para esos momentos reales.
            </p>
            <div className="bg-[#EDF1EA] p-6 rounded-2xl border border-[#A8B5A2]/30" data-id="element-211">
              <p className="text-[#3A3A3A] font-medium text-lg" data-id="element-212">
                Esta versión premium no solo te enseña…{' '}
                <span className="font-bold" data-id="element-213">
                  te guía paso a paso en el momento exacto donde todo ocurre.
                </span>
              </p>
            </div>
          </div>

          {/* PRICING PREMIUM */}
          <div className="bg-[#F7F3EF] text-[#3A3A3A] rounded-3xl p-8 md:p-12 max-w-lg mx-auto shadow-2xl border-2 border-[#D88C7A]/30 text-center relative" data-id="element-214">
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 bg-[#D88C7A] text-white px-6 py-2 rounded-full font-bold shadow-md w-max" data-id="element-215">
              ACCESO PREMIUM COMPLETO
            </div>

            <p className="text-[#3A3A3A]/70 mt-4 mb-6 font-medium" data-id="element-216">
              Todo el contenido + bono exclusivo
            </p>

            <div className="flex justify-center items-baseline gap-2 mb-8" data-id="element-217">
              <span className="text-6xl font-bold text-[#D88C7A]" data-id="element-218">$19.99</span>
              <span className="text-xl text-[#3A3A3A]/50" data-id="element-219">USD</span>
            </div>

            <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional-copia" target="_blank" rel="noopener noreferrer" className="block w-full bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-8 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center mb-6" data-id="element-220">
              QUIERO LA VERSIÓN PREMIUM AHORA
            </a>

            <p className="text-sm text-[#3A3A3A]/60 font-medium mb-4" data-id="element-221">
              ⚠️ Empieza hoy mismo a transformar la forma en que manejas los
              momentos difíciles en casa.
            </p>

            <div className="border-t border-[#E8CFCF]/40 pt-4 mt-4" data-id="element-222">
              <p className="text-xs text-[#3A3A3A]/50 italic" data-id="element-223">
                Este ebook premium es ideal para madres que no solo quieren
                entender a sus hijos… sino saber exactamente qué hacer en cada
                situación.
              </p>
            </div>
          </div>

          {/* TESTIMONIOS PREMIUM */}
          <div className="mt-16" data-id="element-224">
            <h3 className="text-2xl font-bold text-[#3A3A3A] text-center mb-10" data-id="element-225">
              💬 Lo que dicen las mamás sobre el plan premium
            </h3>

            {/* Testimonio destacado */}
            <div className="bg-[#F5EAEA] border-2 border-[#D88C7A]/30 rounded-3xl p-8 md:p-10 mb-8 shadow-lg relative" data-id="element-226">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#D88C7A] text-white px-5 py-1.5 rounded-full font-bold text-sm shadow-md" data-id="element-227">
                ⭐ TESTIMONIO DESTACADO
              </div>
              <p className="text-[#3A3A3A]/80 text-lg italic leading-relaxed mb-4 mt-2" data-id="element-228">
                "Este fue el bono que realmente hizo la diferencia. El ebook me
                ayudó a entender, pero estas tarjetas me dieron herramientas
                reales para el día a día. Ahora cuando mi hijo se frustra, ya sé
                qué hacer."
              </p>
              <div className="flex items-center gap-3" data-id="element-229">
                <div className="w-10 h-10 bg-[#E8CFCF] rounded-full flex items-center justify-center text-[#D88C7A] font-bold" data-id="element-230">
                  C
                </div>
                <div data-id="element-231">
                  <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-232">Carolina</p>
                  <p className="text-[#3A3A3A]/50 text-xs" data-id="element-233">
                    Mamá de niño de 2 años
                  </p>
                </div>
              </div>
            </div>

            {/* Otros testimonios */}
            <div className="grid md:grid-cols-2 gap-6" data-id="element-234">
              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-235">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-236">
                  "Las tarjetas son lo mejor que he comprado para mi hijo. Son
                  tan fáciles de usar que incluso en medio de una rabieta puedo
                  aplicarlas. Se siente todo mucho más tranquilo en casa."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-237">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-238">
                    V
                  </div>
                  <div data-id="element-239">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-240">Valeria</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-241">
                      Mamá de niño de 3 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-242">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-243">
                  "Por fin algo práctico y no solo teoría. Me encantó porque no
                  tengo que pensar qué hacer… simplemente uso las técnicas y
                  funciona."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-244">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-245">
                    D
                  </div>
                  <div data-id="element-246">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-247">Daniela</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-248">
                      Mamá de niño de 4 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-249">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-250">
                  "Mi hijo ahora logra calmarse más rápido. Antes todo terminaba
                  en llanto, ahora con estas técnicas puedo ayudarlo a
                  regularse."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-251">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-252">
                    L
                  </div>
                  <div data-id="element-253">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-254">Laura</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-255">
                      Mamá de niño de 2 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E8CFCF]/40 shadow-sm" data-id="element-256">
                <p className="text-[#3A3A3A]/70 italic leading-relaxed mb-4" data-id="element-257">
                  "Vale completamente la pena el plan premium. Las tarjetas y
                  las técnicas me dieron seguridad como mamá. Es justo lo que
                  necesitaba para dejar de sentirme perdida."
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8CFCF]/30 pt-3" data-id="element-258">
                  <div className="w-9 h-9 bg-[#F5EAEA] rounded-full flex items-center justify-center text-[#D88C7A] font-bold text-sm" data-id="element-259">
                    A
                  </div>
                  <div data-id="element-260">
                    <p className="font-bold text-[#3A3A3A] text-sm" data-id="element-261">Andrea</p>
                    <p className="text-[#3A3A3A]/50 text-xs" data-id="element-262">
                      Mamá de niño de 3 años
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA FINAL PREMIUM */}
            <div className="mt-12 text-center" data-id="element-263">
              <p className="text-[#3A3A3A]/70 font-medium mb-4" data-id="element-264">
                Acceso Premium Completo por solo
              </p>
              <p className="text-5xl font-bold text-[#D88C7A] mb-6" data-id="element-265">
                $19.99 <span className="text-xl text-[#3A3A3A]/50" data-id="element-266">USD</span>
              </p>
              <a href="https://crianza.xn--amordemam-81a.com/products/guia-de-crianza-con-inteligencia-emocional-copia" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#D88C7A] hover:bg-[#C57665] text-white font-bold text-xl py-4 px-10 rounded-xl shadow-lg transition-all transform hover:scale-105 text-center" data-id="element-267">
                QUIERO DESCARGAR TODO AHORA — ACCESO PREMIUM
              </a>
              <p className="mt-4 text-xs text-[#3A3A3A]/40" data-id="element-268">
                🔒 Pago 100% seguro • Acceso inmediato y de por vida 💛
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer data-id="element-269" />
    </div>;
}